import React, { useEffect, useState } from 'react';
import { Search, Hash, Users, MessageSquare, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from '@easyflow/ui-frontend/components/ui/dialog';
import { messengerApi } from '../lib/messengerApi';
import type { QuickSwitchResult, ConversationSummary, ParticipantBrief } from '../types';
import { getAvatarGradient, getInitials } from '../utils/format';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  onPickConversation: (convId: string) => void;
  onPickUser: (user: ParticipantBrief) => void;
}

// Task #1660 — ⌘K / Ctrl+K quick switcher (stile Slack/Linear).
// - input centrale, modal full-screen overlay
// - 1 query → 8 conversazioni + 8 utenti
// - keyboard nav: ↑↓ tra risultati flat, Enter conferma, Esc chiude
// - debounce 150ms per evitare spam server-side
// - mount only when open per non pagare network/listener costantemente

const DEBOUNCE = 150;

type FlatItem =
  | { kind: 'conv'; data: QuickSwitchResult['conversations'][number] }
  | { kind: 'user'; data: ParticipantBrief };

export function QuickSwitcher({ open, onClose, onPickConversation, onPickUser }: Props) {
  const [q, setQ] = useState('');
  const [result, setResult] = useState<QuickSwitchResult>({ conversations: [], users: [] });
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!open) { setQ(''); setResult({ conversations: [], users: [] }); setActive(0); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (!term) { setResult({ conversations: [], users: [] }); return; }
    setLoading(true);
    const t = window.setTimeout(() => {
      messengerApi.quickSearch(term)
        .then((r) => { if (r.success && r.data) setResult(r.data); })
        .finally(() => setLoading(false));
    }, DEBOUNCE);
    return () => window.clearTimeout(t);
  }, [q, open]);

  const flat: FlatItem[] = [
    ...result.conversations.map<FlatItem>((c) => ({ kind: 'conv', data: c })),
    ...result.users.map<FlatItem>((u) => ({ kind: 'user', data: u })),
  ];

  useEffect(() => { setActive(0); }, [result]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') {
        const item = flat[active];
        if (!item) return;
        e.preventDefault();
        if (item.kind === 'conv') onPickConversation(item.data.id);
        else onPickUser(item.data);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flat, active, onPickConversation, onPickUser]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="p-0 gap-0 max-w-xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca conversazioni o persone…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <kbd className="hidden md:inline-block text-[10px] text-muted-foreground bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">Esc</kbd>
        </div>
        <div className="max-h-96 overflow-auto">
          {flat.length === 0 && q.trim() && !loading && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nessun risultato</div>
          )}
          {flat.length === 0 && !q.trim() && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto opacity-30 mb-2" />
              Digita per cercare. ↑↓ per navigare, Invio per aprire.
            </div>
          )}
          {result.conversations.length > 0 && (
            <div>
              <div className="px-4 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-gray-50 dark:bg-gray-900/50">
                Conversazioni
              </div>
              {result.conversations.map((c, idx) => (
                <QsConvRow
                  key={c.id}
                  conv={c}
                  active={active === idx}
                  onClick={() => onPickConversation(c.id)}
                  onHover={() => setActive(idx)}
                />
              ))}
            </div>
          )}
          {result.users.length > 0 && (
            <div>
              <div className="px-4 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-gray-50 dark:bg-gray-900/50">
                Persone
              </div>
              {result.users.map((u, idx) => {
                const flatIdx = result.conversations.length + idx;
                return (
                  <QsUserRow
                    key={u.id}
                    user={u}
                    active={active === flatIdx}
                    onClick={() => onPickUser(u)}
                    onHover={() => setActive(flatIdx)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QsConvRow({ conv, active, onClick, onHover }: {
  conv: QuickSwitchResult['conversations'][number]; active: boolean; onClick: () => void; onHover: () => void;
}) {
  const name = conv.display_title || conv.title || 'Conversazione';
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      onMouseEnter={onHover}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
        active ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-900/50',
      )}
    >
      <div className={cn(
        'h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-semibold shrink-0 bg-gradient-to-br',
        conv.type === 'channel' ? 'from-purple-500 to-indigo-600' : conv.type === 'group' ? 'from-emerald-500 to-teal-600' : getAvatarGradient(name),
      )}>
        {conv.type === 'channel' ? <Hash className="h-4 w-4" /> : conv.type === 'group' ? <Users className="h-4 w-4" /> : getInitials(name.split(' ')[0], name.split(' ')[1])}
      </div>
      <span className="text-sm font-medium truncate">{name}</span>
    </button>
  );
}

function QsUserRow({ user, active, onClick, onHover }: {
  user: ParticipantBrief; active: boolean; onClick: () => void; onHover: () => void;
}) {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Utente';
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      onMouseEnter={onHover}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
        active ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-gray-50 dark:hover:bg-gray-900/50',
      )}
    >
      <div className={cn('h-8 w-8 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-semibold shrink-0', getAvatarGradient(name))}>
        {getInitials(user.first_name, user.last_name)}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{name}</div>
        {user.email && <div className="text-[10px] text-muted-foreground truncate">{user.email}</div>}
      </div>
    </button>
  );
}
